/**
 * Enum value sets and shared constants.
 *
 * These mirror `prisma/schema.prisma` EXACTLY — keep the two in lockstep when
 * changing either. A test in @pospak/db asserts they agree.
 */
export const USER_SCOPES = ['BUSINESS', 'SHOP'];
export const BUSINESS_PROFILES = ['SIMPLE', 'STANDARD', 'ADVANCED'];
export const SETTING_TYPES = [
    'BOOLEAN', 'INT', 'PAISA', 'PERCENT', 'STRING', 'ENUM', 'JSON', 'ID_LIST',
];
export const OVERRIDE_EFFECTS = ['GRANT', 'DENY'];
export const DEPLOYMENT_MODES = ['CLOUD', 'LOCAL'];
/**
 * Optional feature modules a business can have switched on — the ADDON
 * catalog (README.md Part E2, Phase 28).
 *
 * The dividing rule, so every future module lands mechanically: a capability
 * is CORE if switching it off would break a plain cash sale or corrupt shared
 * data; it is an ADDON if the system stays consistent without it. Core is
 * never listed here and can never be withheld — catalog, the stock ledger,
 * selling, shifts/cash, customers, purchasing, settings/users/roles, the basic
 * sales/stock/cash reports, and printing. A shop that cannot print cannot
 * trade, so printing is core no matter what a package says.
 *
 * When an addon is switched OFF the rule is **read stays, write goes**: a
 * business that loses `loyalty` still sees existing point balances on its
 * customers; nothing earns or redeems. The `payroll` gate established this
 * pattern inside the HR router (attendance and employees stay reachable).
 *
 * The first five entries predate Phase 28 and are load-bearing in existing
 * data — `Business.modulesEnabled` rows, `planModule` settings declarations
 * and `requireModule()` mounts all name them — so their spelling must not
 * change.
 */
export const BUSINESS_MODULES = [
    'portal',
    'mobile',
    'payroll',
    'fbr',
    'multishop',
    'delivery',
    'accounting',
    'loyalty',
    'vouchers',
    'wholesale',
    'quotations',
    'receipt-designer',
    'offline',
    'reports-pro',
];
/** Human labels for the Owner portal's package editor. */
export const BUSINESS_MODULE_LABELS = {
    portal: 'Online storefront & orders',
    mobile: 'Mobile-shop / easyload counter',
    payroll: 'Payroll runs',
    fbr: 'FBR digital invoicing',
    multishop: 'Multiple shops, transfers & warehouses',
    delivery: 'Delivery drivers & routes',
    accounting: 'Full double-entry accounting',
    loyalty: 'Loyalty points & tiers',
    vouchers: 'Gift vouchers & store credit',
    wholesale: 'Wholesale pricing & bulk order entry',
    quotations: 'Quotations',
    'receipt-designer': 'Receipt slip designer',
    offline: 'Offline selling & local server',
    'reports-pro': 'Full report catalogue & exports',
};
/**
 * Every module, as a plain mutable array.
 *
 * Still the default for a business created outside the package flow, and the
 * value every pre-Phase-28 business was migrated to, so nothing that existed
 * before packages loses access to anything.
 */
export const ALL_MODULES = [...BUSINESS_MODULES];
export function isBusinessModule(value) {
    return BUSINESS_MODULES.includes(value);
}
/**
 * The one place that answers "what may this business actually use, here?".
 *
 * Both `requireModule()` on the API and `RequireModule` in the web shell
 * resolve through this, so a screen can never be visible for a module the API
 * will refuse (or hidden for one it would have allowed).
 *
 *   package modules (or the legacy list when there is no package)
 *   union addons granted on top
 *   minus the shop's own carve-out (README.md Phase 24)
 *
 * `legacyModules` is the fallback rather than an empty set on purpose: a
 * business with no package must keep behaving exactly as it did before
 * packages existed. An empty list silently plan-locks half the product, which
 * is the bug this whole area exists to prevent.
 */
export function effectiveModules(input) {
    const base = input.packageModules ?? input.legacyModules ?? [];
    const granted = new Set([...base, ...(input.addonModules ?? [])]);
    for (const carvedOut of input.shopModulesDisabled ?? [])
        granted.delete(carvedOut);
    // Filter through the catalog so a stale row naming a module that no longer
    // exists cannot leak through as a truthy string.
    return BUSINESS_MODULES.filter((m) => granted.has(m));
}
/** README.md Phase 24 — RETAIL sells, WAREHOUSE is a stock-only location. */
export const SHOP_KINDS = ['RETAIL', 'WAREHOUSE'];
export const SHOP_CATEGORIES = [
    'RESTAURANT', 'FAST_FOOD', 'PIZZA', 'BBQ', 'CAFE', 'BAKERY', 'SWEETS', 'JUICE_CORNER',
    'GROCERY', 'SUPERMARKET', 'FRUITS_VEGETABLES', 'MEAT_POULTRY', 'DAIRY', 'WATER_SUPPLY',
    'MOBILE_SHOP', 'ELECTRONICS', 'COMPUTERS',
    'CLOTHING', 'FOOTWEAR', 'COSMETICS', 'JEWELLERY', 'TAILOR',
    'PHARMACY', 'OPTICAL', 'CLINIC_LAB',
    'HARDWARE', 'PAINT_SANITARY', 'FURNITURE', 'HOME_APPLIANCES', 'AUTO_PARTS',
    'STATIONERY', 'BOOKS', 'TOYS_GIFTS', 'SPORTS', 'PET_SUPPLIES', 'FLOWERS',
    'SALON', 'LAUNDRY', 'GENERAL_STORE', 'OTHER',
];
export const SHOP_CATEGORY_LABELS = {
    RESTAURANT: 'Restaurant / Desi food',
    FAST_FOOD: 'Fast food / Burgers',
    PIZZA: 'Pizza',
    BBQ: 'BBQ / Karahi',
    CAFE: 'Cafe / Tea',
    BAKERY: 'Bakery',
    SWEETS: 'Sweets / Mithai',
    JUICE_CORNER: 'Juice corner',
    GROCERY: 'Kiryana / Grocery',
    SUPERMARKET: 'Supermarket',
    FRUITS_VEGETABLES: 'Fruits & vegetables',
    MEAT_POULTRY: 'Meat & poultry',
    DAIRY: 'Dairy / Milk shop',
    WATER_SUPPLY: 'Water supply',
    MOBILE_SHOP: 'Mobile shop',
    ELECTRONICS: 'Electronics',
    COMPUTERS: 'Computers & IT',
    CLOTHING: 'Clothing',
    FOOTWEAR: 'Footwear',
    COSMETICS: 'Cosmetics',
    JEWELLERY: 'Jewellery',
    TAILOR: 'Tailor',
    PHARMACY: 'Pharmacy',
    OPTICAL: 'Optical',
    CLINIC_LAB: 'Clinic / Lab',
    HARDWARE: 'Hardware',
    PAINT_SANITARY: 'Paint & sanitary',
    FURNITURE: 'Furniture',
    HOME_APPLIANCES: 'Home appliances',
    AUTO_PARTS: 'Auto parts',
    STATIONERY: 'Stationery',
    BOOKS: 'Books',
    TOYS_GIFTS: 'Toys & gifts',
    SPORTS: 'Sports',
    PET_SUPPLIES: 'Pet supplies',
    FLOWERS: 'Flowers',
    SALON: 'Salon',
    LAUNDRY: 'Laundry',
    GENERAL_STORE: 'General store',
    OTHER: 'Other',
};
export const SHOP_CATEGORY_GROUPS = [
    { key: 'FOOD', label: 'Food & drink', categories: ['RESTAURANT', 'FAST_FOOD', 'PIZZA', 'BBQ', 'CAFE', 'BAKERY', 'SWEETS', 'JUICE_CORNER'] },
    { key: 'GROCERY', label: 'Grocery & daily needs', categories: ['GROCERY', 'SUPERMARKET', 'FRUITS_VEGETABLES', 'MEAT_POULTRY', 'DAIRY', 'WATER_SUPPLY'] },
    { key: 'ELECTRONICS', label: 'Mobile & electronics', categories: ['MOBILE_SHOP', 'ELECTRONICS', 'COMPUTERS'] },
    { key: 'FASHION', label: 'Clothing & personal', categories: ['CLOTHING', 'FOOTWEAR', 'COSMETICS', 'JEWELLERY', 'TAILOR', 'SALON'] },
    { key: 'HEALTH', label: 'Health', categories: ['PHARMACY', 'OPTICAL', 'CLINIC_LAB'] },
    { key: 'HOME', label: 'Home & hardware', categories: ['HARDWARE', 'PAINT_SANITARY', 'FURNITURE', 'HOME_APPLIANCES', 'AUTO_PARTS'] },
    { key: 'OTHER', label: 'Everything else', categories: ['STATIONERY', 'BOOKS', 'TOYS_GIFTS', 'SPORTS', 'PET_SUPPLIES', 'FLOWERS', 'LAUNDRY', 'GENERAL_STORE', 'OTHER'] },
];
export const UNITS_OF_MEASURE = [
    'PCS', 'KG', 'GRAM', 'LITRE', 'ML', 'METRE', 'FOOT', 'DOZEN', 'PACK', 'BOX', 'BAG', 'SERVICE',
];
/** Units that legitimately carry a fractional quantity on a bill line. */
export const FRACTIONAL_UNITS = ['KG', 'GRAM', 'LITRE', 'ML', 'METRE', 'FOOT'];
export const PRICE_MODES = ['EXCL', 'INCL'];
export const BARCODE_SYMBOLOGIES = ['EAN13', 'EAN8', 'UPCA', 'CODE128', 'CODE39', 'QR'];
export const BARCODE_SOURCES = ['SUPPLIER', 'GENERATED', 'MANUAL'];
export const PRICE_LIST_KINDS = ['DEFAULT', 'WHOLESALE', 'STAFF', 'ONLINE', 'PROMO', 'CUSTOM'];
export const STOCK_REASONS = [
    'OPENING', 'PURCHASE_IN', 'PURCHASE_RETURN_OUT', 'SALE_OUT', 'SALE_RETURN_IN',
    'BILL_REVISION_ADJUST', 'TRANSFER_OUT', 'TRANSFER_IN', 'ADJUSTMENT_IN', 'ADJUSTMENT_OUT',
    'DAMAGE_OUT', 'EXPIRY_OUT', 'COUNT_CORRECTION', 'PRODUCTION_IN', 'PRODUCTION_OUT',
];
/** Expected sign of each reason — asserted by the stock service before writing. */
export const STOCK_REASON_SIGN = {
    OPENING: 'IN',
    PURCHASE_IN: 'IN',
    PURCHASE_RETURN_OUT: 'OUT',
    SALE_OUT: 'OUT',
    SALE_RETURN_IN: 'IN',
    BILL_REVISION_ADJUST: 'EITHER',
    TRANSFER_OUT: 'OUT',
    TRANSFER_IN: 'IN',
    ADJUSTMENT_IN: 'IN',
    ADJUSTMENT_OUT: 'OUT',
    DAMAGE_OUT: 'OUT',
    EXPIRY_OUT: 'OUT',
    COUNT_CORRECTION: 'EITHER',
    PRODUCTION_IN: 'IN',
    PRODUCTION_OUT: 'OUT',
};
export const COSTING_METHODS = ['AVERAGE', 'FIFO', 'LAST_COST'];
export const BILL_STATUSES = [
    'DRAFT', 'PARKED', 'UNPAID', 'PAID', 'PARTIALLY_REFUNDED', 'REFUNDED', 'SUPERSEDED', 'VOIDED',
];
/** The ONLY legal bill transitions. Enforced in `bill-status.service`. */
export const BILL_TRANSITIONS = {
    DRAFT: ['PARKED', 'UNPAID', 'VOIDED'],
    PARKED: ['DRAFT', 'UNPAID', 'VOIDED'],
    UNPAID: ['PAID', 'VOIDED'],
    PAID: ['PARTIALLY_REFUNDED', 'REFUNDED', 'SUPERSEDED'],
    PARTIALLY_REFUNDED: ['PARTIALLY_REFUNDED', 'REFUNDED', 'SUPERSEDED'],
    REFUNDED: [],
    SUPERSEDED: [],
    VOIDED: [],
};
export const SALE_CHANNELS = [
    'COUNTER', 'ONLINE_PICKUP', 'ONLINE_DELIVERY', 'PHONE', 'WHOLESALE',
];
export const PAYMENT_METHODS = [
    'CASH', 'CARD', 'WALLET', 'BANK_TRANSFER', 'CREDIT', 'LOYALTY_POINTS',
    'GIFT_VOUCHER', 'STORE_CREDIT', 'CHEQUE', 'OTHER',
];
/** Methods that move the physical cash drawer. */
export const CASH_DRAWER_METHODS = ['CASH'];
export const DISCOUNT_KINDS = [
    'LINE', 'BILL_AMOUNT', 'BILL_PERCENT', 'LOYALTY', 'PROMO', 'COUPON', 'ROUNDING',
];
export const DISCOUNT_BASES = ['PRE_TAX', 'POST_TAX'];
export const REFUND_STATUSES = [
    'DRAFT', 'PENDING_APPROVAL', 'COMPLETED', 'REJECTED', 'VOIDED',
];
export const REFUND_REASONS = [
    'CUSTOMER_CHANGED_MIND', 'DAMAGED', 'WRONG_ITEM', 'EXPIRED', 'PRICE_DISPUTE',
    'BILLING_ERROR', 'ORDER_CANCELLED', 'OTHER',
];
// --- Gift vouchers & store credit (docs/LOYALTY.md's sibling ledgers — a
// pre-paid voucher code, and a per-customer store-credit balance, both
// redeemable as a real `BillPayment` row via `method: 'GIFT_VOUCHER'` /
// `'STORE_CREDIT'`, never as a discount) -------------------------------------
export const GIFT_VOUCHER_STATUSES = ['ACTIVE', 'REDEEMED', 'EXPIRED', 'CANCELLED'];
export const STORE_CREDIT_TXN_KINDS = ['ISSUE', 'REDEEM', 'ADJUSTMENT', 'REFUND_ISSUE'];
/** Reasons whose returned goods must NOT go back into sellable stock by default. */
export const NON_RESTOCKABLE_REASONS = ['DAMAGED', 'EXPIRED'];
export const SHIFT_STATUSES = ['OPEN', 'CLOSED', 'REOPENED'];
export const CASH_MOVEMENT_KINDS = [
    'OPENING_FLOAT', 'SALE_CASH', 'REFUND_CASH', 'PAY_IN', 'PAY_OUT', 'DROP_TO_SAFE',
    'BANK_DEPOSIT', 'MOBILE_CASH_IN', 'MOBILE_CASH_OUT', 'CLOSING_COUNT', 'ADJUSTMENT',
];
export const PARTY_KINDS = ['CUSTOMER', 'SUPPLIER', 'EMPLOYEE', 'OTHER'];
export const LOYALTY_TXN_KINDS = [
    'OPENING', 'EARN', 'REDEEM', 'REDEEM_REVERSAL', 'CLAWBACK', 'EXPIRE',
    'ADJUSTMENT', 'WRITE_OFF', 'TRANSFER_IN', 'TRANSFER_OUT',
];
export const FULFILMENT_TYPES = ['PICKUP', 'DELIVERY'];
export const ORDER_STATUSES = [
    'PLACED', 'ACCEPTED', 'PREPARING', 'READY_FOR_PICKUP', 'OUT_FOR_DELIVERY',
    'DELIVERED', 'COMPLETED', 'REJECTED', 'CANCELLED',
];
/** The ONLY legal order transitions. Enforced in `order-status.service`. */
export const ORDER_TRANSITIONS = {
    PLACED: ['ACCEPTED', 'REJECTED', 'CANCELLED'],
    ACCEPTED: ['PREPARING', 'READY_FOR_PICKUP', 'OUT_FOR_DELIVERY', 'CANCELLED'],
    PREPARING: ['READY_FOR_PICKUP', 'OUT_FOR_DELIVERY', 'CANCELLED'],
    READY_FOR_PICKUP: ['COMPLETED', 'CANCELLED'],
    OUT_FOR_DELIVERY: ['DELIVERED', 'CANCELLED'],
    DELIVERED: ['COMPLETED'],
    COMPLETED: [],
    REJECTED: [],
    CANCELLED: [],
};
export const ACCOUNT_TYPES = ['ASSET', 'LIABILITY', 'EQUITY', 'INCOME', 'EXPENSE'];
/** System accounts seeded for every business — referenced by `Account.systemKey`. */
export const SYSTEM_ACCOUNTS = {
    CASH_IN_HAND: { code: '1010', name: 'Cash in Hand', type: 'ASSET' },
    CASH_AT_BANK: { code: '1020', name: 'Cash at Bank', type: 'ASSET' },
    INVENTORY: { code: '1030', name: 'Inventory', type: 'ASSET' },
    ACCOUNTS_RECEIVABLE: { code: '1040', name: 'Accounts Receivable', type: 'ASSET' },
    COMMISSION_RECEIVABLE: { code: '1050', name: 'Commission Receivable', type: 'ASSET' },
    ACCOUNTS_PAYABLE: { code: '2010', name: 'Accounts Payable', type: 'LIABILITY' },
    SALES_TAX_PAYABLE: { code: '2020', name: 'Sales Tax Payable', type: 'LIABILITY' },
    LOYALTY_LIABILITY: { code: '2030', name: 'Loyalty Points Liability', type: 'LIABILITY' },
    POS_FEE_PAYABLE: { code: '2040', name: 'POS Service Fee Payable', type: 'LIABILITY' },
    OWNER_EQUITY: { code: '3010', name: "Owner's Equity", type: 'EQUITY' },
    OWNER_DRAWINGS: { code: '3020', name: "Owner's Drawings", type: 'EQUITY' },
    SALES: { code: '4010', name: 'Sales', type: 'INCOME' },
    SALES_RETURNS: { code: '4020', name: 'Sales Returns', type: 'INCOME' },
    DISCOUNTS_ALLOWED: { code: '4030', name: 'Discounts Allowed', type: 'INCOME' },
    COMMISSION_INCOME: { code: '4040', name: 'Commission Income', type: 'INCOME' },
    COGS: { code: '5010', name: 'Cost of Goods Sold', type: 'EXPENSE' },
    EXPENSE_RENT: { code: '6010', name: 'Rent', type: 'EXPENSE' },
    EXPENSE_UTILITIES: { code: '6020', name: 'Utilities', type: 'EXPENSE' },
    EXPENSE_SALARIES: { code: '6030', name: 'Salaries & Wages', type: 'EXPENSE' },
    EXPENSE_TRANSPORT: { code: '6040', name: 'Transport & Delivery', type: 'EXPENSE' },
    EXPENSE_REPAIRS: { code: '6050', name: 'Repairs & Maintenance', type: 'EXPENSE' },
    EXPENSE_MISC: { code: '6090', name: 'Miscellaneous', type: 'EXPENSE' },
    COST_ROUNDING: { code: '6099', name: 'Cost Rounding', type: 'EXPENSE' },
    SUSPENSE: { code: '9999', name: 'Suspense', type: 'ASSET' },
};
export const MOBILE_ACCOUNT_KINDS = ['EASYLOAD', 'WALLET', 'BANK', 'FRANCHISE'];
/** Optional — distinguishes multiple lines of the same wallet brand (docs/Pospak-vs-Dukaan-Cross-Check-Report.docx §3.2). */
export const MOBILE_WALLET_CATEGORIES = ['MERCHANT', 'PERSONAL', 'MASTER', 'AGENT'];
export const MOBILE_PROVIDERS = [
    'JAZZ', 'ZONG', 'UFONE', 'TELENOR', 'SCOM',
    'EASYPAISA', 'JAZZCASH', 'UPAISA', 'NAYAPAY', 'SADAPAY',
    'BANK', 'OTHER',
];
export const MOBILE_TXN_KINDS = [
    'EASYLOAD', 'SCRATCH_CARD', 'CASH_IN', 'CASH_OUT', 'SEND_MONEY', 'RECEIVE_MONEY',
    'BILL_PAYMENT', 'FLOAT_TOPUP', 'FLOAT_WITHDRAW', 'ADJUSTMENT',
];
/**
 * Expected sign of the drawer and float effect for each mobile transaction
 * kind (docs/MOBILE-SHOP.md §3). `0` = no effect, `1` = increases, `-1` =
 * decreases. `null` = free (adjustments). Asserted before every write.
 */
export const MOBILE_TXN_EFFECT = {
    EASYLOAD: { cash: 1, float: -1 },
    SCRATCH_CARD: { cash: 1, float: 0 },
    CASH_IN: { cash: 1, float: -1 },
    CASH_OUT: { cash: -1, float: 1 },
    SEND_MONEY: { cash: 1, float: -1 },
    RECEIVE_MONEY: { cash: -1, float: 1 },
    BILL_PAYMENT: { cash: 1, float: -1 },
    FLOAT_TOPUP: { cash: null, float: 1 },
    FLOAT_WITHDRAW: { cash: 1, float: -1 },
    ADJUSTMENT: { cash: null, float: null },
};
export const MOBILE_DIRECTIONS = ['SEND', 'RECEIVE'];
export const MOBILE_TXN_STATUSES = ['PENDING', 'SUCCESS', 'FAILED', 'REVERSED'];
export const ATTENDANCE_STATUSES = [
    'PRESENT', 'LATE', 'HALF_DAY', 'ABSENT', 'LEAVE', 'HOLIDAY', 'OFF',
];
export const FBR_MODES = ['SANDBOX', 'PRODUCTION'];
export const FBR_DOCUMENT_TYPES = ['SALE_INVOICE', 'DEBIT_NOTE', 'CREDIT_NOTE'];
export const FBR_SUBMISSION_STATUSES = [
    'QUEUED', 'IN_FLIGHT', 'ACCEPTED', 'REJECTED', 'RETRY', 'FAILED', 'CANCELLED',
];
/** FBR gateway endpoints (docs/FBR.md §2). */
export const FBR_ENDPOINTS = {
    HOST: 'https://gw.fbr.gov.pk',
    POST_INVOICE: '/di_data/v1/di/postinvoicedata',
    POST_INVOICE_SANDBOX: '/di_data/v1/di/postinvoicedata_sb',
    VALIDATE_INVOICE: '/di_data/v1/di/validateinvoicedata',
    VALIDATE_INVOICE_SANDBOX: '/di_data/v1/di/validateinvoicedata_sb',
    PROVINCES: '/pdi/v1/provinces',
    DOC_TYPE_CODE: '/pdi/v1/doctypecode',
    ITEM_DESC_CODE: '/pdi/v1/itemdesccode',
    SRO_ITEM_CODE: '/pdi/v1/sroitemcode',
    TRANS_TYPE_CODE: '/pdi/v1/transtypecode',
    UOM: '/pdi/v1/uom',
    SRO_SCHEDULE: '/pdi/v1/SroSchedule',
    SALE_TYPE_TO_RATE: '/pdi/v2/SaleTypeToRate',
    HS_UOM: '/pdi/v2/HS_UOM',
    SRO_ITEM: '/pdi/v2/SROItem',
    STATL: '/dist/v1/statl',
    GET_REG_TYPE: '/dist/v1/Get_Reg_Type',
};
/** Retry backoff for FBR submissions, in seconds (docs/FBR.md §6). */
export const FBR_RETRY_BACKOFF_SECONDS = [15, 60, 300, 900, 3_600];
export const FBR_RETRY_MAX_HOURS = 24;
export const APPROVAL_KINDS = [
    'REFUND', 'DISCOUNT', 'PRICE_OVERRIDE', 'BILL_REVISION', 'STOCK_ADJUSTMENT',
    'SHIFT_VARIANCE', 'MOBILE_VARIANCE', 'EXPENSE', 'PAYROLL',
];
export const APPROVAL_STATUSES = ['PENDING', 'APPROVED', 'REJECTED', 'CANCELLED'];
/** What a `PrintDevice` prints — a receipt printer, a label printer, or a report/A4 printer. */
export const PRINT_DEVICE_KINDS = ['RECEIPT', 'LABEL', 'REPORT'];
/** `ESCPOS`/`TSPL`/`ZPL` are what the print agent (README.md Phase 17.4) speaks to the
 *  physical printer; `SYSTEM` means "hand it to the OS print dialog", i.e. no agent needed. */
export const PRINT_PROTOCOLS = ['ESCPOS', 'TSPL', 'ZPL', 'SYSTEM'];
export const PRINT_DEVICE_CONNECTIONS = ['USB', 'NETWORK', 'SERIAL'];
/* -------------------------------------------------------------------------- */
/* Human-visible ID series                                                     */
/* -------------------------------------------------------------------------- */
export const ID_SERIES = {
    BILL: 'BILL',
    REFUND: 'REFUND',
    ORDER: 'ORDER',
    GRN: 'GRN',
    PURCHASE_ORDER: 'PO',
    PURCHASE_RETURN: 'PRET',
    TRANSFER: 'TRANSFER',
    STOCK_COUNT: 'COUNT',
    EXPENSE: 'EXPENSE',
    MOBILE_TXN: 'MOBILE',
    BARCODE: 'BARCODE',
    QUOTATION: 'QUOTATION',
    DELIVERY_RUN: 'DELIVERY_RUN',
    SUPPLIER_PAYMENT: 'SPAY',
};
/** Prefix letter each series prints with, e.g. `S01-000123` for a bill at shop S01. */
export const ID_SERIES_PREFIX = {
    BILL: 'S',
    REFUND: 'R',
    ORDER: 'O',
    GRN: 'G',
    PO: 'PO',
    PRET: 'PR',
    TRANSFER: 'T',
    COUNT: 'C',
    EXPENSE: 'E',
    MOBILE: 'M',
    BARCODE: 'B',
    QUOTATION: 'Q',
    DELIVERY_RUN: 'DR',
    SPAY: 'SP',
};
/* -------------------------------------------------------------------------- */
/* Misc                                                                        */
/* -------------------------------------------------------------------------- */
export const DEFAULT_TIMEZONE = 'Asia/Karachi';
export const DEFAULT_CURRENCY = 'PKR';
/** JWT audiences — a token issued for one portal is worthless in another. */
export const JWT_AUDIENCE = {
    STAFF: 'pospak-web',
    OWNER: 'pospak-owner',
    PORTAL: 'pospak-portal',
    AGENT: 'pospak-agent',
    /**
     * The client portal, where a business's contact person works. A fifth
     * mutually exclusive audience for the same reason the owner has one: a token
     * minted for one surface must be intrinsically useless on the others.
     */
    CLIENT: 'pospak-client',
};
/** Header the web app uses to declare which shop it is acting on. */
export const SHOP_HEADER = 'x-pospak-shop';
/** Realtime event names — always `domain:action`. */
export const SOCKET_EVENTS = {
    BILL_PAID: 'bill:paid',
    BILL_VOIDED: 'bill:voided',
    BILL_REVISED: 'bill:revised',
    REFUND_CREATED: 'refund:created',
    ORDER_PLACED: 'order:placed',
    ORDER_UPDATED: 'order:updated',
    STOCK_LOW: 'stock:low',
    STOCK_CHANGED: 'stock:changed',
    SHIFT_OPENED: 'shift:opened',
    SHIFT_CLOSED: 'shift:closed',
    MOBILE_LOW_FLOAT: 'mobile:low-float',
    FBR_ACCEPTED: 'fbr:accepted',
    FBR_REJECTED: 'fbr:rejected',
    APPROVAL_REQUESTED: 'approval:requested',
    SYNC_STATUS: 'sync:status',
};
//# sourceMappingURL=constants.js.map